import moduleColors, { ModuleColorKey } from "~/themes/moduleColors";

export interface RecentDestination {
	hours: string[];
	icon?: string;
	label: string;
	labelKey?: string;
	module: string;
	path: string;
	section?: string;
}

export const RECENT_DESTINATIONS_KEY = "worksuite-home-recent-destinations";

const MAX_RECENT_DESTINATIONS = 5;
const MAX_TRACKED_DESTINATIONS = 20;
const WINDOW_DAYS = 14;

const moduleColorKeyBySegment: Record<string, ModuleColorKey> = {
	availability: "AVAILABILITY",
	benchmark: "BENCHMARK",
	count: "COUNT",
	engage: "ENGAGE",
	events: "EVENTS",
	fulfill: "FULFILL",
	intelligence: "INTELLIGENCE",
	material: "MATERIAL",
	portal: "PORTAL",
	produce: "PRODUCE",
	reports: "REPORTS",
	restock: "RESTOCK",
	verify: "VERIFY"
};

const iconBySegment: Record<string, string> = {
	availability: "event_available",
	benchmark: "leaderboard",
	count: "qr_code_scanner",
	engage: "storefront",
	events: "event_note",
	fulfill: "local_shipping",
	intelligence: "insights",
	material: "inventory_2",
	portal: "cottage",
	produce: "potted_plant",
	reports: "summarize",
	restock: "autorenew",
	verify: "verified"
};

export const colorForModule = (module: string): string => {
	const colorKey = moduleColorKeyBySegment[module];
	return colorKey ? moduleColors[colorKey] : moduleColors.WORKSUITE;
};

export const iconForModule = (module: string): string => iconBySegment[module] ?? "history";

const toDayKey = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const nowKey = (): string => {
	const date = new Date();
	return `${toDayKey(date)}T${String(date.getHours()).padStart(2, "0")}`;
};

export const isModuleRootRedirect = (cameFrom: string | null, moduleSegment: string): boolean =>
	cameFrom === `/${moduleSegment}`;

const windowStart = (now: string): string => {
	const start = new Date(`${now.slice(0, 10)}T00:00:00`);
	start.setDate(start.getDate() - (WINDOW_DAYS - 1));
	return `${toDayKey(start)}T00`;
};

const hoursWithinWindow = (hours: string[], now: string): string[] => {
	const start = windowStart(now);
	return hours.filter((hour) => hour >= start && hour <= now);
};

const destinationKey = (destination: { module: string; path: string; section?: string }): string =>
	`${destination.module}::${destination.section ?? destination.path}`;

const isRecentDestination = (value: unknown): value is RecentDestination => {
	if (typeof value !== "object" || value === null) return false;
	if (!("label" in value) || !("module" in value) || !("path" in value) || !("hours" in value)) return false;
	return (
		typeof value.label === "string" &&
		typeof value.module === "string" &&
		typeof value.path === "string" &&
		Array.isArray(value.hours) &&
		value.hours.every((hour) => typeof hour === "string") &&
		(!("labelKey" in value) || typeof value.labelKey === "string")
	);
};

export const loadRecentDestinations = (): RecentDestination[] => {
	try {
		const stored = localStorage.getItem(RECENT_DESTINATIONS_KEY);
		if (!stored) return [];
		const parsed: unknown = JSON.parse(stored);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isRecentDestination);
	} catch {
		return [];
	}
};

const lastHour = (destination: RecentDestination): string => destination.hours[destination.hours.length - 1] ?? "";

export const topRecentDestinations = (
	destinations: RecentDestination[],
	now: string,
	limit = MAX_RECENT_DESTINATIONS
): RecentDestination[] =>
	destinations
		.map((destination) => ({ ...destination, hours: hoursWithinWindow(destination.hours, now) }))
		.filter((destination) => destination.hours.length > 0)
		.sort((a, b) => b.hours.length - a.hours.length || lastHour(b).localeCompare(lastHour(a)))
		.slice(0, limit);

const recordVisit = (
	existing: RecentDestination[],
	visit: Omit<RecentDestination, "hours">,
	now: string
): RecentDestination[] => {
	const key = destinationKey(visit);
	const prior = existing.find((destination) => destinationKey(destination) === key);
	const hours = hoursWithinWindow([...new Set([...(prior?.hours ?? []), now])], now).sort();
	const others = existing
		.filter((destination) => destinationKey(destination) !== key)
		.map((destination) => ({ ...destination, hours: hoursWithinWindow(destination.hours, now) }))
		.filter((destination) => destination.hours.length > 0);
	return [{ ...visit, hours }, ...others].slice(0, MAX_TRACKED_DESTINATIONS);
};

export default recordVisit;
